import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useState } from 'react';

import { Button, CloseButton, Drawer, Portal } from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons/linear';

import { FiltersDrawerProps } from '@app/components/organisms/filters-drawer';
import { useMapSearch } from '@app/hooks/useMapSearch';

export const FiltersDrawer = ({
    isOpen,
    children,
    onApplyFilters,
    onClearFilters,
}: FiltersDrawerProps) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'filters',
    });

    const [isSearchLoading, setIsSearchLoading] = useState(false);

    const { isFiltersContentVisible, setFiltersVisibility } = useMapSearch();

    useEffect(() => {
        if (!isOpen || !isFiltersContentVisible) {
            setIsSearchLoading(false);
        }
    }, [isOpen, isFiltersContentVisible]);

    const handleDrawerClose = useCallback(() => {
        setIsSearchLoading(true);
        setFiltersVisibility(false);
        onApplyFilters();
    }, [setFiltersVisibility, onApplyFilters]);

    return (
        <Drawer.Root
            open={isOpen || isFiltersContentVisible}
            placement="bottom"
            lazyMount={false}
            unmountOnExit={false}
            onOpenChange={details => {
                if (!details.open) {
                    handleDrawerClose();
                }
            }}
        >
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner>
                    <Drawer.Content>
                        <Drawer.Header borderBottomWidth={1}>
                            <Drawer.Title>{t('filter')}</Drawer.Title>
                            <Drawer.CloseTrigger asChild>
                                <CloseButton colorPalette={'secondary'}>
                                    <IconsLinear.Close />
                                </CloseButton>
                            </Drawer.CloseTrigger>
                        </Drawer.Header>
                        <Drawer.Body h="100%" overflowY="auto">
                            {children}
                        </Drawer.Body>
                        <Drawer.Footer borderTopWidth={1} gap={4}>
                            <Button flex={1} onClick={onClearFilters}>
                                {t('clear')}
                            </Button>
                            <Button
                                flex={1}
                                variant={'solid'}
                                onClick={handleDrawerClose}
                                loading={isSearchLoading}
                            >
                                {t('search')}
                            </Button>
                        </Drawer.Footer>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
};
